import 'reflect-metadata';
import { injectable } from 'inversify';
import {
  publication_book_army_all,
  publication_book_army_dto,
  publication_book_army_full,
  publication_book_last_update,
} from '@Api';
import {
  ArmyBook,
  ArmyBookVersioning,
  CategoryBook,
  MagicItemBook,
  ModelRuleBook,
  SharedMagicItemBook,
  UnitBook,
  UnitDefensiveProfileBook,
  UnitGlobalProfileBook,
  UnitOptionBook,
  UnitOptionParameterBook,
  UnitProfileBook,
  UnitProfileRuleBook,
} from '@Models';
import { IMapper } from '../IMapper';

@injectable()
export class ArmyBookMapper
  implements
    IMapper<
      string,
      publication_book_army_all,
      publication_book_army_full,
      ArmyBook
    >
{
  public mapAll(item: publication_book_army_all): ArmyBook {
    return {
      id: item.id,
      name: item.name,
      prefix: item.prefix ?? '',
      version: item.version ?? '',
      logoPath: item.logoPath,
      updatedAt: item.updated_at,
      modelRules: [],
      magicItems: [],
      sharedMagicItems: [],
      armyOrganisation: [],
      armyList: [],
      loc: {
        en: {},
      },
    } satisfies ArmyBook as ArmyBook;
  }

  public mapFull(item: publication_book_army_full): ArmyBook {
    return {
      id: item.id,
      name: item.name,
      prefix: item.prefix ?? '',
      version: item.version ?? '',
      logoPath: item.logoPath,
      updatedAt: item.updated_at,
      armyOrganisation: item.armyOrganisation.map(
        (org) =>
          ({
            name: org.name,
            logo: org.logo,
            value: org.value,
            minimum: org.minimum,
          }) satisfies CategoryBook as CategoryBook,
      ),
      modelRules: item.modelRules.map(
        (rule) =>
          ({
            name: rule.name,
            inherits: rule.inherits ?? null,
            type: rule.type,
          }) satisfies ModelRuleBook as ModelRuleBook,
      ),
      magicItems: item.magicItems.map(
        (magic) =>
          ({
            name: magic.name,
            type: magic.type,
            rarity: magic.rarity,
            cost: magic.cost,
            support: magic.support,
            dominant: magic.dominant,
            restriction: magic.restriction,
          }) satisfies MagicItemBook as MagicItemBook,
      ),
      sharedMagicItems: item.sharedMagicItems.map(
        (magic) =>
          ({
            name: magic.name,
            type: magic.type,
            inherits: magic.inherits,
            cost: magic.cost,
          }) satisfies SharedMagicItemBook as SharedMagicItemBook,
      ),
      armyList: item.armyList.map(
        (unit) =>
          ({
            name: unit.name,
            inherits: unit.inherits ?? undefined,
            categories: unit.categories,
            unitSize: unit.unitSize,
            baseSize: unit.baseSize
              ? {
                  width: unit.baseSize.width,
                  depth: unit.baseSize.depth,
                }
              : undefined,
            cost: unit.cost,
            height: unit.height,
            fluff: unit.fluff ?? null,
            profile: unit.profile
              ? ({
                  global: unit.profile.global
                    ? ({
                        chargeRate: unit.profile.global.chargeRate,
                        marchRate: unit.profile.global.marchRate,
                        discipline: unit.profile.global.discipline,
                        rules:
                          unit.profile.global.rules?.map((rule) => {
                            if (typeof rule === 'string') {
                              return rule;
                            }
                            return {
                              name: rule.name,
                              parameters: rule.parameters,
                            } satisfies UnitProfileRuleBook as UnitProfileRuleBook;
                          }) ?? undefined,
                      } satisfies UnitGlobalProfileBook as UnitGlobalProfileBook)
                    : undefined,
                  defensive: unit.profile.defensive
                    ? ({
                        hp: unit.profile.defensive.hp,
                        defensiveSkill: unit.profile.defensive.defensiveSkill,
                        resistance: unit.profile.defensive.resistance,
                        armour: unit.profile.defensive.armour,
                        rules:
                          unit.profile.defensive.rules?.map((rule) => {
                            if (typeof rule === 'string') {
                              return rule;
                            }
                            return {
                              name: rule.name,
                              parameters: rule.parameters,
                            } satisfies UnitProfileRuleBook as UnitProfileRuleBook;
                          }) ?? undefined,
                      } satisfies UnitDefensiveProfileBook as UnitDefensiveProfileBook)
                    : undefined,
                } satisfies UnitProfileBook as UnitProfileBook)
              : undefined,
            options:
              unit.options?.map(
                (option) =>
                  ({
                    name: option.name,
                    parameters: {
                      list:
                        option.parameters?.list?.map(
                          (param) =>
                            ({
                              name: param.name,
                              cost: param.cost ?? undefined,
                              costpermodel: param.costpermodel ?? undefined,
                              parameters: param.parameters ?? undefined,
                            }) satisfies UnitOptionParameterBook as UnitOptionParameterBook,
                        ) ?? [],
                      optionChoiceLimits:
                        option.parameters?.optionChoiceLimits ?? undefined,
                    },
                  }) satisfies UnitOptionBook as UnitOptionBook,
              ) ?? undefined,
            wizardconclave: unit.wizardconclave
              ? ({
                  parameters: {
                    spelllist:
                      unit.wizardconclave.parameters?.spelllist?.map(
                        (param) =>
                          ({
                            name: param.name,
                          }) satisfies UnitOptionParameterBook as UnitOptionParameterBook,
                      ) ?? [],
                  },
                } satisfies UnitOptionBook as UnitOptionBook)
              : undefined,
            commandgroup: unit.commandgroup
              ? ({
                  parameters: {
                    list:
                      unit.commandgroup.parameters?.list?.map(
                        (param) =>
                          ({
                            name: param.name,
                            cost: param.cost ?? undefined,
                            costpermodel: param.costpermodel ?? undefined,
                            parameters: param.parameters ?? undefined,
                          }) satisfies UnitOptionParameterBook as UnitOptionParameterBook,
                      ) ?? [],
                  },
                } satisfies UnitOptionBook as UnitOptionBook)
              : undefined,
          }) satisfies UnitBook as UnitBook,
      ),
      loc: {
        en: item.loc.en,
      },
    } satisfies ArmyBook as ArmyBook;
  }

  public map(item: publication_book_army_full): ArmyBook {
    return this.mapFull(item);
  }

  public mapDto(item: ArmyBook): publication_book_army_dto {
    return {
      name: item.name,
      prefix: item.prefix,
    } satisfies publication_book_army_dto as publication_book_army_dto;
  }

  public mapVersioning(item: publication_book_last_update): ArmyBookVersioning {
    return {
      id: item.id,
      name: item.name,
      updatedAt: item.updated_at,
    } satisfies ArmyBookVersioning as ArmyBookVersioning;
  }
}
